import {
    pipe,
    pathOr,
    head,
    propOr,
    defaultTo,
    propEq,
    flatten,
    filter,
    curryN,
    path
} from 'ramda'
import { useState } from 'react'

import { CardCollection } from './CardCollection.js'
import ReactHtmlParser from 'react-html-parser'

const isExternalLink = propEq('type', 'external-links')
const isVideo = propEq('type', 'videos')

const getFirstTaxonomyTerm = curryN(2, (taxonomy, post) => pipe(
    pathOr([], ['_embedded', 'wp:term']),
    flatten,
    filter(propEq('taxonomy', taxonomy)),
    head,
    defaultTo({}),
    propOr('', 'name')
)(post))

const parseFlatPostTerms = (taxonomy, post) => {
    return post?.terms?.filter(term => term.taxonomy === taxonomy)
}

const getFirstTermFromPostTerms = (taxonomy, post) => {
    return parseFlatPostTerms(taxonomy, post)?.find(Boolean) || undefined
}

const getFirstIndustryTerm = getFirstTaxonomyTerm('industry')

const getLinkLabel = (post) => {
    if (isExternalLink(post)) {
        return 'Read More'
    }

    if (isVideo(post)) {
        return 'Watch'
    }

    return 'learn more'
}

const getLinkIconByPostType = (post) => {
    if (isExternalLink(post)) {
        return 'newWindow'
    }

    if (isVideo(post)) {
        return 'play'
    }

    return 'forward'
}

const getCustomerLogo = pipe(
    defaultTo(''),
    path(['acf', 'customer_logo'])
)

// get the first featuredmedia for the provided post
const getFeaturedMedia = (post) => {
    if (post.featuredImage) {
        return post.featuredImage
    }
    const featuredMediaList = post?._embedded['wp:featuredmedia']
    if (featuredMediaList && featuredMediaList.length > 0) {
        return featuredMediaList[0]
    }
    return null
}

const getBody = (post) => {
    // if post is link, return description
    if (isExternalLink(post)) {
        return post.acf.description
    }

    // if excerpt is string, return it
    if (typeof post.excerpt === 'string') {
        return post.excerpt
    }

    // if excerpt is object, return rendered excerpt
    if (typeof post.excerpt === 'object') {
        return post.excerpt?.rendered || ''
    }

    return ''
}

/** parse isFeatured */
const parseIsFeatured = (post) => {
    return post.isFeatured || post?.acf?.is_featured || false
}

/** returns the title string from the post object */
const parseTitle = (post) => {
    if (typeof post.title === 'string') {
        return ReactHtmlParser(post.title)
    }

    return post?.title?.raw || ReactHtmlParser(post?.title?.rendered)
}


const parseCardFromPost = (post) => ({

    type: post.type,
    title: parseTitle(post),
    // TODO: reimplement titleImage
    // titleImage: showTitleImage ? getCustomerLogo(post) : '',
    date: post.date,
    id: post.id,
    href: isExternalLink(post) ? post.acf.url : post.link,
    linkLabel: getLinkLabel(post),
    linkIcon: getLinkIconByPostType(post),
    linkIconPosition: isVideo(post) ? 'left' : 'right',
    isFeatured: parseIsFeatured(post),
    image: {
        src: getFeaturedMedia(post)?.source_url || '',
        alt: getFeaturedMedia(post)?.alt_text || ''
    },
    badge: {
        label: ReactHtmlParser(getFirstTermFromPostTerms('industry', post)?.name || getFirstIndustryTerm(post))
    },
    body: getBody(post),
    eventDetails: post?.type === 'event' ? {} : null

})


export function PostCollection(props = {}) {
    // deconstruct props
    const {
        columns = 3,
        rows = 2,
        posts = [],
        onLoadMoreClick,
        isLoading = false,
        loadMoreEnabled = false,
        page,
    } = props

    return (
        <div className={`max-w-screen-xl mx-auto grid gap-4 ${isLoading && posts.length < 1 ? 'opacity-40' : ''}`}>
            <div className="grid gap-4">
                <CardCollection cards={posts.map(parseCardFromPost)} columns={columns} rows={rows} page={page} />
            </div>
            {
                loadMoreEnabled && (
                    <div className="text-center">
                        <button
                            onClick={onLoadMoreClick}
                            className="px-4 py-2 text-base font-semibold text-white rounded cursor-pointer bg-blue hover:bg-blue-darker">{isLoading ? 'Loading...' : 'Load More'}</button>
                    </div>
                )
            }
        </div>
    )
}

export default PostCollection


